<template>
  <div ref="widget"></div>
</template>
<script>
// Cloudflare Turnstile in managed mode, rendered explicitly so the form can reset it
// between attempts (tokens are single use). It sits beside reCAPTCHA, and the server
// refuses a donation without a passing token, so submit waits for one (waitForToken).
// Nothing shows unless Cloudflare wants an interaction.
function loadTurnstile(callback) {
  if (window.turnstile) {
    callback();
    return;
  }
  window.wiqTurnstileCallbacks = window.wiqTurnstileCallbacks || [];
  window.wiqTurnstileCallbacks.push(callback);
  if (document.getElementById('cf-turnstile-script')) {
    return;
  }

  window.wiqTurnstileLoaded = () => {
    window.wiqTurnstileCallbacks.forEach((cb) => cb());
    window.wiqTurnstileCallbacks = [];
  };
  const script = document.createElement('script');
  script.id = 'cf-turnstile-script';
  script.async = true;
  script.defer = true;
  script.setAttribute('src', 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit&onload=wiqTurnstileLoaded');
  document.head.appendChild(script);
}

export default {
  name: 'turnstile',
  props: {
    siteKey: {
      type: String,
      required: true,
    },
    // Lowercase letters, digits, - and _ only, up to 32 characters
    action: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      widgetId: null,
      token: null,
      // True while Cloudflare is showing the donor a challenge
      interactive: false,
    };
  },
  mounted() {
    const vm = this;
    loadTurnstile(() => {
      if (!vm.$refs.widget) {
        return;
      }
      vm.widgetId = window.turnstile.render(vm.$refs.widget, {
        sitekey: vm.siteKey,
        action: vm.action,
        appearance: 'interaction-only',
        callback: (token) => { vm.token = token; },
        'expired-callback': () => { vm.token = null; },
        'error-callback': () => { vm.token = null; vm.interactive = false; },
        'before-interactive-callback': () => { vm.interactive = true; },
        'after-interactive-callback': () => { vm.interactive = false; },
      });
    });
  },
  beforeDestroy() {
    if (this.widgetId !== null && window.turnstile) {
      window.turnstile.remove(this.widgetId);
    }
  },
  methods: {
    getToken() {
      return this.token;
    },
    // A donor can reach submit before the widget has finished (Oct 28: one arrived with no token),
    // so give it a few seconds, or up to interactiveTimeoutMs while they're solving a challenge.
    // Resolves with whatever token there is, possibly null.
    waitForToken(timeoutMs = 8000, interactiveTimeoutMs = 120000) {
      const vm = this;
      const startedAt = Date.now();
      return new Promise((resolve) => {
        const check = () => {
          const waited = Date.now() - startedAt;
          const timedOut = waited >= (vm.interactive ? interactiveTimeoutMs : timeoutMs);
          if (vm.token || timedOut) {
            resolve(vm.token);
          } else {
            setTimeout(check, 200);
          }
        };
        check();
      });
    },
    // A token can only be verified once, so get a fresh one after every submit
    reset() {
      this.token = null;
      if (this.widgetId !== null && window.turnstile) {
        window.turnstile.reset(this.widgetId);
      }
    },
  },
};
</script>
